/**
 * Batch-6 verlof — structureel weekpatroon voor ouderschapsverlof
 * ("elke woensdagmiddag van 01-09 t/m 31-12"). Eén `LeaveRequest` dekt alle
 * patroon-dagen; de submit expandeert het patroon server-side naar
 * individuele dagen die in `LeaveRequest.structureelPatroon` (JSON) worden
 * vastgelegd. Planning/capaciteit gebruiken `patroonDektDatum` zodat alléén
 * de patroon-dagen blokkeren, niet het hele datumbereik.
 *
 * Datums zijn UTC-middernacht van de NL-kalenderdag (repo-conventie).
 */

import { isFeestdag, round2, type PublicHolidayInfo } from './leave-calculations'

/** ISO-weekdag: 1 = maandag … 7 = zondag. */
export type IsoWeekdag = 1 | 2 | 3 | 4 | 5 | 6 | 7

/** JSON-shape zoals opgeslagen in `LeaveRequest.structureelPatroon`. */
export interface StructureelPatroonJson {
  /** ISO-weekdag (1 = maandag … 7 = zondag). */
  weekdag: number
  /** Uren per patroon-dag; null = de volledige roosteruren van die dag. */
  urenPerKeer: number | null
  /** yyyy-mm-dd */
  vanDatum: string
  /** yyyy-mm-dd */
  totDatum: string
  /** Server-side geëxpandeerde dagen (feestdagen/0-urendagen al weggefilterd). */
  dagen: Array<{ datum: string; uren: number }>
}

/** getUTCDay (0=zo..6=za) → ISO-weekdag (1=ma..7=zo). */
export function naarIsoWeekdag(utcDay: number): IsoWeekdag {
  return (utcDay === 0 ? 7 : utcDay) as IsoWeekdag
}

/**
 * Alle kalenderdatums (UTC-middernacht) in [van, tot] die op de gevraagde
 * ISO-weekdag vallen en GEEN feestdag zijn. Roosteruren (0-urendagen) worden
 * door de caller gefilterd — die kent `contractUrenOpDatum`.
 */
export function bepaalPatroonDatums(
  patroon: { weekdag: number; vanDatum: Date; totDatum: Date },
  feestdagen: PublicHolidayInfo[],
): Date[] {
  const datums: Date[] = []
  const cursor = new Date(
    Date.UTC(
      patroon.vanDatum.getUTCFullYear(),
      patroon.vanDatum.getUTCMonth(),
      patroon.vanDatum.getUTCDate(),
    ),
  )
  const eind = new Date(
    Date.UTC(
      patroon.totDatum.getUTCFullYear(),
      patroon.totDatum.getUTCMonth(),
      patroon.totDatum.getUTCDate(),
    ),
  )
  while (cursor.getTime() <= eind.getTime()) {
    if (
      naarIsoWeekdag(cursor.getUTCDay()) === patroon.weekdag &&
      !isFeestdag(cursor, feestdagen, true)
    ) {
      datums.push(new Date(cursor))
    }
    cursor.setUTCDate(cursor.getUTCDate() + 1)
  }
  return datums
}

/**
 * Spec 218 — alle kalenderdatums (UTC-middernacht) in [van, tot] die op één van
 * de gevraagde ISO-weekdagen vallen, elke `elkeNWeken` weken (1 = elke week,
 * 2 = om de week). De weektelling start in de ISO-week van `vanDatum`, dus "om
 * de week vanaf wo 23-09" pakt 23-09, 07-10, 21-10, …
 *
 * Bewust GEEN feestdag-filter: de caller toont een feestdag als overgeslagen
 * dag in de preview (zichtbaar i.p.v. stil weggelaten).
 */
export function bepaalWeekpatroonDatums(patroon: {
  weekdagen: number[]
  vanDatum: Date
  totDatum: Date
  elkeNWeken?: number
}): Date[] {
  const n = Math.max(1, Math.floor(patroon.elkeNWeken ?? 1))
  const dagMs = 24 * 60 * 60 * 1000
  const utcMiddernacht = (d: Date): Date =>
    new Date(Date.UTC(d.getUTCFullYear(), d.getUTCMonth(), d.getUTCDate()))
  const maandagVan = (d: Date): number => d.getTime() - (naarIsoWeekdag(d.getUTCDay()) - 1) * dagMs
  const van = utcMiddernacht(patroon.vanDatum)
  const eind = utcMiddernacht(patroon.totDatum)
  const startWeek = maandagVan(van)
  const weekdagen = new Set(patroon.weekdagen)
  const datums: Date[] = []
  for (let t = van.getTime(); t <= eind.getTime(); t += dagMs) {
    const d = new Date(t)
    if (!weekdagen.has(naarIsoWeekdag(d.getUTCDay()))) continue
    const weekIndex = Math.round((maandagVan(d) - startWeek) / (7 * dagMs))
    if (weekIndex % n !== 0) continue
    datums.push(d)
  }
  return datums
}

/**
 * Uren voor één patroon-dag: gevraagde uren gecapt op de roosteruren van die
 * dag (bestaande dagdeel-regel); zonder `urenPerKeer` de volledige
 * roosteruren. 0-urendag → 0 (caller laat de dag dan vallen).
 */
export function patroonDagUren(roosterUren: number, urenPerKeer: number | null): number {
  if (roosterUren <= 0) return 0
  if (urenPerKeer === null || urenPerKeer <= 0) return round2(roosterUren)
  return round2(Math.min(urenPerKeer, roosterUren))
}

/** Som van de geëxpandeerde dag-uren (de server-autoritaire aanvraag-uren). */
export function patroonTotaalUren(dagen: Array<{ uren: number }>): number {
  return round2(dagen.reduce((som, d) => som + d.uren, 0))
}

/** yyyy-mm-dd van een UTC-kalenderdag. */
export function patroonDatumKey(datum: Date): string {
  return datum.toISOString().slice(0, 10)
}

/**
 * Dekt dit patroon de gegeven kalenderdag? Planning-/capaciteitscode gebruikt
 * dit om een structureel-patroon-aanvraag alléén op de patroon-dagen te laten
 * blokkeren (een woensdag-patroon blokkeert geen donderdag).
 */
export function patroonDektDatum(
  patroon: Pick<StructureelPatroonJson, 'dagen'> | null | undefined,
  datum: Date,
): boolean {
  if (!patroon || !Array.isArray(patroon.dagen)) return true
  const key = patroonDatumKey(datum)
  return patroon.dagen.some((d) => d.datum === key)
}

/**
 * Parse een onbekende JSON-waarde (uit Prisma `Json`) naar het patroon-shape;
 * null wanneer het geen geldig patroon is. Bewust tolerant: alleen de velden
 * die de lezers nodig hebben worden gecontroleerd.
 */
export function parseStructureelPatroon(value: unknown): StructureelPatroonJson | null {
  if (!value || typeof value !== 'object') return null
  const v = value as Record<string, unknown>
  if (typeof v.weekdag !== 'number' || !Array.isArray(v.dagen)) return null
  const dagen: Array<{ datum: string; uren: number }> = []
  for (const d of v.dagen) {
    if (!d || typeof d !== 'object') return null
    const dag = d as Record<string, unknown>
    if (typeof dag.datum !== 'string' || typeof dag.uren !== 'number') return null
    dagen.push({ datum: dag.datum, uren: dag.uren })
  }
  return {
    weekdag: v.weekdag,
    urenPerKeer: typeof v.urenPerKeer === 'number' ? v.urenPerKeer : null,
    vanDatum: typeof v.vanDatum === 'string' ? v.vanDatum : '',
    totDatum: typeof v.totDatum === 'string' ? v.totDatum : '',
    dagen,
  }
}
